<script setup lang="ts">
import { toast } from "vue-sonner";
import { editorDisplayStates } from "./editorResolve";
import { parseNavigationDisplayStatesJson } from "./navigationDisplayStatesRaw";

const props = defineProps<{
  blockId: string;
  open: boolean;
}>();

const emit = defineEmits<{
  "update:open": [value: boolean];
}>();

const { t } = useI18n({ useScope: "local" });
const { block, patch } = useInlineBlockEditor(props.blockId);

const dialogOpen = computed({
  get: () => props.open,
  set: (value: boolean) => emit("update:open", value),
});
const isFullscreen = shallowRef(false);
const localJson = shallowRef("");
const monacoOptions = {
  automaticLayout: true,
  wordWrap: "on",
  minimap: { enabled: false },
};
const drawerWidthClass = computed(() =>
  isFullscreen.value
    ? "data-[vaul-drawer-direction=right]:w-screen data-[vaul-drawer-direction=right]:sm:max-w-none"
    : "data-[vaul-drawer-direction=right]:w-[50vw] data-[vaul-drawer-direction=right]:sm:max-w-[800px]",
);

watch(
  () => props.open,
  (open) => {
    if (!open) return;
    localJson.value = JSON.stringify(
      editorDisplayStates(block.value?.displayStates),
      null,
      2,
    );
    isFullscreen.value = false;
  },
  { immediate: true },
);

function save() {
  try {
    patch({ displayStates: parseNavigationDisplayStatesJson(localJson.value) });
    dialogOpen.value = false;
  } catch {
    toast.error(t("invalidJsonError"));
  }
}
</script>

<template>
  <Drawer v-model:open="dialogOpen" direction="right">
    <DrawerContent
      :class="['flex h-full flex-col transition-[width] duration-200', drawerWidthClass]"
    >
      <DrawerHeader class="flex flex-row items-start justify-between">
        <div>
          <DrawerTitle>{{ t("title") }}</DrawerTitle>
          <DrawerDescription>{{ t("description") }}</DrawerDescription>
        </div>
        <Button
          variant="ghost"
          size="icon"
          class="size-8 shrink-0"
          @click="isFullscreen = !isFullscreen"
        >
          <Icon
            mode="svg"
            size="16"
            :name="isFullscreen ? 'lucide:minimize-2' : 'lucide:maximize-2'"
          />
        </Button>
      </DrawerHeader>

      <div class="flex-1 overflow-hidden px-4">
        <ClientOnly>
          <MonacoEditor
            v-model="localJson"
            lang="json"
            :options="monacoOptions"
            class="h-full w-full rounded-sm border"
          >
            {{ t("loading") }}
          </MonacoEditor>
        </ClientOnly>
      </div>

      <DrawerFooter>
        <Button @click="save">{{ t("save") }}</Button>
        <DrawerClose as-child>
          <Button variant="outline">{{ t("cancel") }}</Button>
        </DrawerClose>
      </DrawerFooter>
    </DrawerContent>
  </Drawer>
</template>

<i18n lang="yaml">
en:
  title: "Raw display states"
  description: "Copy or replace only this block's display states. Links, variants, and other settings are not included."
  loading: "Loading..."
  save: "Save display states"
  cancel: "Cancel"
  invalidJsonError: "Display states must be a non-empty JSON array with a unique ID and name for every state."
</i18n>
